import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  LayoutChangeEvent,
} from "react-native";
import { ms } from "./moderateScale";
import { Colors } from "@/constants/Colors";
import { useUISlice } from "@/models/useUISlice";
import Icon from "./Icon";
import { useUserSlice } from "@/models/useUserSlice";
import { useTranslation } from "react-i18next";
import { AUDIO_LANGUAGES } from "@/constants";
import { getLocale } from "@/models/utils";
import { EDITOR_WIDTH } from "@/pages";
import { useRef, useState } from "react";
import { AIEditOption } from "@/models/types";

function ButtonDictationMobile({
  isListening,
  onMicPress,
  editOptions,
  selectedTabOptions,
  onCopyPress,
  onSharePress,
}: {
  isListening: boolean;
  onMicPress: ({ language }: { language: string }) => void;
  selectedTabOptions: AIEditOption[];
  editOptions: { label: string; value: AIEditOption; onPress: () => void }[];
  onCopyPress: () => void;
  onSharePress: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useUISlice.getState().device.isMobile;
  const width = useUISlice.getState().device.width;
  const user = useUserSlice.getState().userData;
  const scrollViewRef = useRef<ScrollView>(null);
  const [scrollViewWidth, setScrollViewWidth] = useState(0);
  const language =
    user?.preferences?.transcriptionLanguage || getLocale().slice(0, 2);

  const _onLanguageChange = (language: string) => {
    useUserSlice
      .getState()
      .user()
      .update({
        updates: {
          preferences: {
            ...(user?.preferences || {}),
            transcriptionLanguage: language,
          },
        },
      });
  };

  const handleEditOptionPress = (
    option: { onPress: () => void },
    index: number
  ) => {
    option.onPress();
    const tabWidth = ms(isMobile ? 100 : 120); // Approximate width of each tab including gap
    const scrollX = index * tabWidth - scrollViewWidth / 2 + tabWidth / 2;
    scrollViewRef.current?.scrollTo({
      x: Math.max(0, scrollX),
      animated: true,
    });
  };

  const onScrollViewLayout = (event: LayoutChangeEvent) => {
    setScrollViewWidth(event.nativeEvent.layout.width);
  };

  return (
    <View
      style={{
        bottom: ms(0),
        left: 0,
        height: ButtonDictationMobile.HEIGHT,
        right: 0,
        justifyContent: "center",
        alignItems: "center",
        ...{ position: "fixed" as any },
      }}
    >
      {editOptions.length === 0 && (
        <View style={{ width: "100%", height: ms(30 + 8) }} />
      )}
      {editOptions.length > 0 && (
        <View>
          <Text
            style={{
              fontSize: ms(13),
              color: Colors.text,
              textAlign: "center",
              marginBottom: ms(-5),
            }}
          >
            ▲
          </Text>

          <ScrollView
            ref={scrollViewRef}
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            style={{
              backgroundColor: Colors.text,
              borderRadius: ms(4),
              height: ms(30),
              maxHeight: ms(30),
              marginBottom: ms(8),
              ...(isMobile && {
                width: width - ms(16),
                maxWidth: width - ms(16),
                marginHorizontal: "auto",
              }),
            }}
            onLayout={onScrollViewLayout}
            contentContainerStyle={{
              flexDirection: "row",
              gap: ms(16),
              paddingHorizontal: ms(16),
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {editOptions.map((option, index) => (
              <Pressable
                key={`tab-${index}-${option.value}`}
                onPress={() => handleEditOptionPress(option, index)}
                style={{
                  borderRightWidth: StyleSheet.hairlineWidth,
                  borderRightColor: Colors.white + "40",
                  height: ms(30),
                  alignItems: "center",
                  justifyContent: "center",
                  paddingRight: ms(16),
                  ...(index === editOptions.length - 1 && {
                    borderRightWidth: 0,
                    paddingRight: 0,
                  }),
                }}
              >
                <Text
                  style={{
                    fontSize: ms(14),
                    color: Colors.white,
                    fontWeight: "300",
                    textDecorationColor: Colors.white,
                    ...(selectedTabOptions.includes(option.value) && {
                      fontWeight: "400",
                      textDecorationLine: "underline",
                    }),
                    ...(isMobile && {
                      fontSize: ms(14),
                    }),
                  }}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}

      <View
        style={{
          width: "100%",
          maxWidth: EDITOR_WIDTH - ms(24),
          marginHorizontal: "auto",
          alignItems: "center",
          justifyContent: "center",
          borderTopLeftRadius: ms(40),
          borderTopRightRadius: ms(40),
          paddingTop: ms(30),
          paddingBottom: ms(40),
          backgroundColor: Colors.accent + "10",
        }}
      >
        <Pressable
          onPress={onCopyPress}
          style={{
            alignItems: "center",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: ms(30),
          }}
        >
          <View
            style={{
              width: ms(60),
              height: ms(60),
              alignItems: "center",
              justifyContent: "center",
              borderRadius: ms(100),
              backgroundColor: "#c3e7ff",
            }}
          >
            <Icon
              icon="copy"
              style={{
                width: ms(20),
                height: ms(20),
                tintColor: Colors.text,
              }}
            />
          </View>
          <Text
            style={{ fontSize: ms(12), color: Colors.text, marginTop: ms(8) }}
          >
            {t("buttonDictationMobile.copy")}
          </Text>
        </Pressable>

        <Pressable
          style={{
            width: ms(90),
            height: ms(90),
            borderRadius: ms(100),
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: Colors.accent,
            ...(isListening && {
              backgroundColor: Colors.error,
            }),
          }}
          onPress={() => onMicPress({ language })}
        >
          {isListening && (
            <View
              style={{
                width: ms(40),
                height: ms(40),
                borderRadius: ms(8),
                backgroundColor: Colors.white,
              }}
            />
          )}

          {!isListening && (
            <Icon
              icon="mic"
              style={{
                width: ms(40),
                height: ms(40),
                tintColor: Colors.white,
              }}
            />
          )}
        </Pressable>

        <Pressable
          onPress={onSharePress}
          style={{
            alignItems: "center",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: ms(30),
          }}
        >
          <View
            style={{
              width: ms(60),
              height: ms(60),
              alignItems: "center",
              justifyContent: "center",
              borderRadius: ms(100),
              backgroundColor: "#c3e7ff",
            }}
          >
            <Icon
              icon="share"
              style={{
                width: ms(22),
                height: ms(22),
                tintColor: Colors.text,
              }}
            />
          </View>
          <Text
            style={{ fontSize: ms(12), color: Colors.text, marginTop: ms(8) }}
          >
            {t("buttonDictationMobile.share")}
          </Text>
        </Pressable>

        <select
          style={{
            marginTop: ms(16),
            fontSize: ms(12),
            border: "none",
            color: Colors.accent,
            fontWeight: "500",
            backgroundColor: Colors.accent + "10",
            width: ms(100),
            borderRadius: 0,
          }}
          value={language}
          onChange={(e) => _onLanguageChange(e.target.value)}
        >
          {AUDIO_LANGUAGES.map((language) => (
            <option key={language.value} value={language.value}>
              {language.label}
            </option>
          ))}
        </select>
      </View>
    </View>
  );
}

ButtonDictationMobile.HEIGHT = useUISlice.getState().device.isMobile
  ? ms(290)
  : ms(290);

export default ButtonDictationMobile;
